import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

export default function Home() {
  const { user } = useAuth();

  return (
    <Layout>
      <SEO
        title="B2B CSV - AI-Powered Business Data Export | Lead Generation Tool"
        description="Find and export B2B business data across the United States. AI-powered search with CSV export for lead generation and sales prospecting. Get verified business contacts, phone numbers, emails, and social media profiles."
        canonical="/"
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="text-center">
                <h1 className="text-4xl tracking-tight font-extrabold text-slate-100 sm:text-5xl md:text-6xl">
                  <span className="block">B2B Lead Generation</span>
                  <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Export to CSV</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-slate-300 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                  AI-powered business search across the United States. Find prospects, enrich data, and export to CSV for your sales pipeline.
                </p>
                <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                  {user ? (
                    <div className="rounded-md shadow-lg">
                      <Link
                        to="/search"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 md:py-4 md:text-lg md:px-10 transition-all"
                      >
                        Start Searching
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-md shadow-lg">
                        <Link
                          to="/register"
                          className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 md:py-4 md:text-lg md:px-10 transition-all"
                        >
                          Get Started Free
                        </Link>
                      </div>
                      <div className="mt-3 rounded-md shadow-lg sm:mt-0 sm:ml-3">
                        <Link
                          to="/pricing"
                          className="w-full flex items-center justify-center px-8 py-3 border border-slate-600 text-base font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 md:py-4 md:text-lg md:px-10 transition-all"
                        >
                          View Pricing
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-cyan-400 font-semibold tracking-wide uppercase">Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-slate-100 sm:text-4xl">
              Everything you need for B2B lead generation
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {/* Feature 1 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  🔍
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">AI-Powered Search</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Find businesses with intelligent AI search to optimize your queries and discover ideal prospects.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  📊
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">Data Enrichment</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Enrich business data with emails, social media profiles, and contact information (Pro plan).
                </p>
              </div>

              {/* Feature 3 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  🗺️
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">Route Optimization</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Plan efficient routes to visit multiple prospects for field sales and door-to-door campaigns.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  📁
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">CSV Export</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Export your results to CSV for seamless integration with CRMs, spreadsheets, and marketing tools.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  🇺🇸
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">US-Wide Coverage</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Search for businesses anywhere in the United States with comprehensive nationwide coverage.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-2xl">
                  ⚡
                </div>
                <h3 className="mt-4 text-lg leading-6 font-medium text-slate-100">Fast & Reliable</h3>
                <p className="mt-2 text-base text-slate-300 text-center">
                  Get results in seconds with enterprise-grade infrastructure and reliable data sources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Tutorial Section */}
      <div className="py-12 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-100 mb-3">See It In Action</h2>
            <p className="text-lg text-slate-300">Watch how easy it is to find and export B2B leads</p>
          </div>
          <div className="aspect-video rounded-xl overflow-hidden shadow-2xl border border-slate-700">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube-nocookie.com/embed/HBuKFHPyzxg?rel=0&modestbranding=1"
              title="B2B CSV Tutorial - How to Export Business Leads"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-8 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-100 mb-2">How It Works</h2>
            <p className="text-lg text-slate-300">Find leads in 3 simple steps</p>
          </div>

          {/* Step 1: Search */}
          <div className="mb-8">
            <div className="flex items-center justify-center mb-4">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-xl">
                1
              </span>
              <h3 className="ml-3 text-xl font-bold text-slate-100">Enter Your Search Query</h3>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-5">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Search for businesses</label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <input
                      type="text"
                      placeholder="e.g., coffee shops in Denver"
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 text-sm min-w-0"
                      disabled
                    />
                    <button className="px-5 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium text-sm whitespace-nowrap">
                      Search
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 text-sm">
                  <button className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs">
                    Use My Location
                  </button>
                  <select className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg border border-slate-600 text-xs">
                    <option>Results: 20</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Review Results */}
          <div className="mb-8">
            <div className="flex items-center justify-center mb-4">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-xl">
                2
              </span>
              <h3 className="ml-3 text-xl font-bold text-slate-100">Review & Select Results</h3>
            </div>
            <div className="max-w-5xl mx-auto">
              <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-5">
                <div className="mb-3 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-2">
                  <span className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium text-sm text-center sm:text-left">
                    3 of 20 selected
                  </span>
                  <div className="flex gap-2">
                    <button className="flex-1 sm:flex-initial px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium text-sm whitespace-nowrap">
                      Save Search
                    </button>
                    <button className="flex-1 sm:flex-initial px-3 py-1.5 bg-green-600 text-white rounded-lg font-medium text-sm whitespace-nowrap">
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Mock Business Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { name: "Blue Bottle Coffee", address: "1234 Main St, Denver, CO", phone: "(555) 123-4567" },
                    { name: "Starbucks Reserve", address: "5678 Oak Ave, Denver, CO", phone: "(555) 234-5678" },
                    { name: "Copper Door Coffee", address: "9101 Pine Rd, Denver, CO", phone: "(555) 345-6789" },
                  ].map((business, idx) => (
                    <div key={idx} className="p-3 rounded-lg border-2 border-blue-500 bg-slate-800">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-slate-100 text-sm">{business.name}</h4>
                        <input type="checkbox" checked className="h-4 w-4 text-blue-500 rounded" disabled />
                      </div>
                      <div className="space-y-1 text-xs text-slate-300">
                        <p>{business.address}</p>
                        <p>{business.phone}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Export */}
          <div className="mb-4">
            <div className="flex items-center justify-center mb-4">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-xl">
                3
              </span>
              <h3 className="ml-3 text-xl font-bold text-slate-100">Export to CSV</h3>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-500/20 mb-3">
                    <svg className="h-10 w-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-100 mb-2">Download Ready!</h4>
                  <p className="text-slate-300 mb-4 text-sm">Your CSV includes:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm max-w-md mx-auto mb-4">
                    {['Business Names', 'Addresses', 'Phone Numbers', 'Websites', 'Email Addresses', 'Social Media Links'].map((item, idx) => (
                      <div key={idx} className="flex items-center text-slate-300 text-xs">
                        <svg className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {item}
                      </div>
                    ))}
                  </div>
                  <button className="w-full sm:w-auto px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base">
                    Download business-search.csv
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to grow your sales pipeline?</span>
            <span className="block">Start generating leads today.</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-blue-100">
            Free tier includes 20 business results per day. No credit card required.
          </p>
          <Link
            to="/register"
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-slate-50 sm:w-auto transition-colors shadow-lg"
          >
            Sign up for free
          </Link>
        </div>
      </div>
    </Layout>
  );
}
